import type { UseQueryResult } from '@tanstack/react-query';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import type { JobLevel } from '~/shared/data-contract/job-level';
import type { LanguageCode } from '~/shared/internationalization';

import type { ApplicationFetchers } from '../../fetchers/create-application-fetchers';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { useJobArchitectureContentLanguage } from '../providers/JobArchitectureLanguageProvider';
import { jobArchitectureKeys } from './query-keys';

const createQueryFn =
    (fetchJobLevels: ApplicationFetchers['fetchJobLevels']) =>
    (jobArchitectureId: string, jobRoleId: string, acceptLanguage: LanguageCode) =>
    async ({ signal }: { signal: AbortSignal }): Promise<readonly JobLevel[]> => {
        const result = await fetchJobLevels(jobArchitectureId, jobRoleId, { acceptLanguage, signal });

        if (result.isOk === true) {
            return result.value;
        }

        throw result.error;
    };

export const useJobLevels = (
    jobArchitectureId: string,
    jobRoleId: string,
    options?: { readonly enabled?: boolean },
): UseQueryResult<readonly JobLevel[], Error> => {
    const { fetchJobLevels } = useApplicationFetchers();
    const acceptLanguage = useJobArchitectureContentLanguage();

    return useQuery({
        enabled: options?.enabled,
        queryFn: createQueryFn(fetchJobLevels)(jobArchitectureId, jobRoleId, acceptLanguage),
        queryKey: jobArchitectureKeys.levelsByRole(jobArchitectureId, jobRoleId),
        staleTime: 10_000,
        throwOnError: false,
    });
};

export const usePrefetchJobLevels = (): ((jobArchitectureId: string, jobRoleId: string) => void) => {
    const queryClient = useQueryClient();
    const { fetchJobLevels } = useApplicationFetchers();
    const acceptLanguage = useJobArchitectureContentLanguage();

    return (jobArchitectureId: string, jobRoleId: string) =>
        queryClient.prefetchQuery({
            queryFn: createQueryFn(fetchJobLevels)(jobArchitectureId, jobRoleId, acceptLanguage),
            queryKey: jobArchitectureKeys.levelsByRole(jobArchitectureId, jobRoleId),
        });
};

/** One levels query per role, sharing the cache entries of useJobLevels. */
export const useJobLevelsByRoles = (jobArchitectureId: string, jobRoleIds: readonly string[]) => {
    const { fetchJobLevels } = useApplicationFetchers();
    const acceptLanguage = useJobArchitectureContentLanguage();

    return useQueries({
        queries: jobRoleIds.map((jobRoleId) => ({
            queryFn: async () => {
                const result = await fetchJobLevels(jobArchitectureId, jobRoleId, { acceptLanguage });
                if (result.isOk) {
                    return result.value;
                }
                throw result.error;
            },
            queryKey: jobArchitectureKeys.levelsByRole(jobArchitectureId, jobRoleId),
            throwOnError: false,
        })),
    });
};
